import { BorderBox, FlexBox, Text, TextInline } from '@trmediaab/zebra';
import { useWindowSize } from '@trmediaab/zebra-hooks';
import PropTypes from 'prop-types';

import { GridTable } from '~/components/GridTable';
import { Bridle } from '~/components/StartList/Bridle';
import { Carriage } from '~/components/StartList/Carriage';
import { Shoes } from '~/components/StartList/Shoes';
import { VARIANT_THRESHOLDS } from '~/components/StartList/utils';
import StartNumberBadge from '~/components/StartNumberBadge';
import { spacing } from '~/utils/css-mixins';
import { parseBridle, parseCarriage, parseShoes } from '~/utils/equipment';

const getGridTemplateColumns = (leg, legs, betType, isMobile) => [
  {
    label: legs?.length > 0 ? `${betType}-${leg}` : ' NR',
    columnWidth: 'max-content',
    pl: 1,
  },
  {
    label: 'HÄST',
    columnWidth: '160px',
  },
  {
    label: 'VÄRDERING',
    columnWidth: 'max-content',
  },
  {
    label: 'BALANS',
    columnWidth: 'max-content',
  },
  {
    label: 'VAGN',
    columnWidth: isMobile ? '1fr' : 'max-content',
    textAlign: isMobile ? 'left' : undefined,
  },
  ...(!isMobile
    ? [
        {
          label: 'HUVUDLAG',
          columnWidth: '1fr',
          textAlign: 'right',
        },
      ]
    : []),
];

const valuationColors = {
  negative: '#EF2565',
  positive: '#7CCB24',
  neutral: '#DADAD6',
};

const toListData = (preliminaryEquipments, betType, isMobile) =>
  preliminaryEquipments.map(preliminaryEquipment => {
    const cells = [];

    cells.push(
      <StartNumberBadge
        start_number={preliminaryEquipment.start.start_number}
        bet_type={betType}
      />,

      <Text fontSize="1" fontWeight="semiBold">
        {preliminaryEquipment.start.horse.name}
      </Text>,

      <BorderBox
        width="10px"
        height="10px"
        borderRadius="circle"
        mx="auto"
        bg={valuationColors[preliminaryEquipment.valuation]}
      />,

      <Shoes
        data={parseShoes(preliminaryEquipment)}
        fontSize="1"
        fontWeight="bold"
        tooltipEnabled
      />,

      <Carriage
        data={parseCarriage(preliminaryEquipment)}
        fontSize="1"
        fontWeight="bold"
        tooltipEnabled
      />,
    );

    if (!isMobile) {
      cells.push(
        <Bridle
          data={parseBridle(preliminaryEquipment)}
          fontSize="1"
          fontWeight="normal"
          ml="auto"
          textAlign="right"
          tooltipEnabled
        />,
      );
    }

    return {
      cells,
      ...((isMobile || preliminaryEquipment.text != null) && {
        extraRow: {
          wrapperProps: {
            gridColumnStart: 2,
          },
          content: (
            <FlexBox gap="1" flexDirection="column">
              {isMobile && (
                <FlexBox alignItems="center" width="fit-content">
                  <Text
                    fontSize="1"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    <TextInline fontWeight="semiBold">Huvudlag:</TextInline>
                    &nbsp;
                    <Bridle
                      data={parseBridle(preliminaryEquipment)}
                      fontSize="1"
                      display="inline"
                      ml="auto"
                      tooltipEnabled
                    />
                  </Text>
                </FlexBox>
              )}
              {preliminaryEquipment.text != null && (
                <Text fontSize="1">
                  <TextInline fontWeight="semiBold">Övrigt:</TextInline>
                  <TextInline color="text.tertiary" fontStyle="italic">
                    &nbsp;{preliminaryEquipment.text}
                  </TextInline>
                </Text>
              )}
            </FlexBox>
          ),
        },
      }),
    };
  });

const PreliminaryEquipmentsTable = ({
  bet_type,
  preliminaryEquipments,
  leg,
  legs,
}) => {
  const { width: windowWidth } = useWindowSize();

  if (preliminaryEquipments.length === 0) {
    return null;
  }

  const isMobile = windowWidth < VARIANT_THRESHOLDS.mobile;
  const gridTemplateColumns = getGridTemplateColumns(
    leg,
    legs,
    bet_type,
    isMobile,
  );

  return (
    <GridTable
      maxWidth={spacing.siteContentWidth}
      labels={gridTemplateColumns.map(({ label, textAlign, pl }, idx) => (
        <Text key={idx} textAlign={textAlign} pl={pl}>
          {label}
        </Text>
      ))}
      data={toListData(preliminaryEquipments, bet_type, isMobile)}
      gridTemplateColumns={gridTemplateColumns
        .map(({ columnWidth }) => columnWidth)
        .join(' ')}
      showDivider
    />
  );
};

PreliminaryEquipmentsTable.propTypes = {
  bet_type: PropTypes.string.isRequired,
  preliminaryEquipments: PropTypes.array.isRequired,
};

export default PreliminaryEquipmentsTable;
